import { useEffect, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Plus, Trash2, FileText, Copy, BookmarkPlus } from 'lucide-react'
import { useApi, asArray } from '../hooks/useApi'
import { apiPost, apiDelete } from '../lib/auth'
import {
  listTemplates,
  getMappingPlan,
  createReportFromTemplate,
  saveReportAsTemplate,
  type ReportTemplate,
  type MappingPlan,
} from '../features/reports/reportTemplatesApi'
import TemplateMappingModal from '../features/reports/TemplateMappingModal'

interface Report {
  id: number
  name: string
  headerColor?: string
  startDate?: string
  endDate?: string
  updatedAt: string
  createdAt: string
}

export default function Reports() {
  const navigate = useNavigate()
  const { projectId } = useParams<{ projectId: string }>()
  const { data: reports, loading, error, refetch } = useApi<Report[]>(
    `/api/reporting/clients/${projectId}/reports`,
  )

  const [creatingName, setCreatingName] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [duplicatingId, setDuplicatingId] = useState<number | null>(null)

  const [mode, setMode] = useState<'blank' | 'template'>('blank')
  const [templates, setTemplates] = useState<ReportTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null)
  const [mappingPlan, setMappingPlan] = useState<MappingPlan | null>(null)
  const [savingTemplateId, setSavingTemplateId] = useState<number | null>(null)
  const [savedTemplateName, setSavedTemplateName] = useState<string | null>(null)

  // Échelle de la miniature : 1100 px (largeur native du canevas) réduits pour
  // couvrir la largeur d'une carte en grille 3 colonnes (~1100*0.34 ≈ 374 px).
  const THUMB_SCALE = 0.34

  async function loadTemplates() {
    try {
      const list = await listTemplates()
      setTemplates(Array.isArray(list) ? list : [])
    } catch {
      setTemplates([])
    }
  }

  useEffect(() => {
    if (showCreate) loadTemplates()
  }, [showCreate])

  if (error === 'Unauthorized' || error === '401') {
    navigate('/login')
    return null
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (mode === 'template') {
      await handleCreateFromTemplate()
      return
    }
    setCreating(true)
    setCreateError(null)
    const { data, error: err } = await apiPost<Report>(
      `/api/reporting/clients/${projectId}/reports`,
      { name: creatingName.trim() || undefined },
    )
    setCreating(false)
    if (err) {
      setCreateError(err)
      return
    }
    if (data) {
      navigate(`/p/${projectId}/reports/${data.id}`)
    }
  }

  async function handleCreateFromTemplate() {
    if (!selectedTemplateId) {
      setCreateError('Sélectionne un template.')
      return
    }
    setCreating(true)
    setCreateError(null)
    try {
      const plan = await getMappingPlan(selectedTemplateId, Number(projectId))
      if (plan.ambiguous.length > 0) {
        setMappingPlan(plan)
        setCreating(false)
        return
      }
      const r = await createReportFromTemplate(Number(projectId), selectedTemplateId, undefined)
      setCreating(false)
      if (r) navigate(`/p/${projectId}/reports/${r.id}`)
    } catch (err) {
      setCreating(false)
      setCreateError(err instanceof Error ? err.message : 'Erreur')
    }
  }

  async function handleConfirmMapping(mapping: Record<string, number>) {
    if (!selectedTemplateId) return
    setMappingPlan(null)
    setCreating(true)
    setCreateError(null)
    try {
      const r = await createReportFromTemplate(Number(projectId), selectedTemplateId, mapping)
      setCreating(false)
      if (r) navigate(`/p/${projectId}/reports/${r.id}`)
    } catch (err) {
      setCreating(false)
      setCreateError(err instanceof Error ? err.message : 'Erreur')
    }
  }

  async function handleSaveAsTemplate(e: React.MouseEvent, id: number) {
    e.preventDefault()
    e.stopPropagation()
    const name = window.prompt('Nom du template')
    if (!name || !name.trim()) return
    setSavingTemplateId(id)
    try {
      await saveReportAsTemplate(id, name.trim())
      await loadTemplates()
      setSavedTemplateName(name.trim())
      setTimeout(() => setSavedTemplateName(null), 3000)
    } catch {
      // échec silencieux, pas de crash
    } finally {
      setSavingTemplateId(null)
    }
  }

  async function handleDelete(e: React.MouseEvent, id: number) {
    e.preventDefault()
    e.stopPropagation()
    if (!confirm('Supprimer ce rapport ?')) return
    setDeletingId(id)
    await apiDelete(`/api/reporting/clients/${projectId}/reports/${id}`)
    setDeletingId(null)
    refetch()
  }

  async function handleDuplicate(e: React.MouseEvent, id: number) {
    e.preventDefault()
    e.stopPropagation()
    setDuplicatingId(id)
    const { data } = await apiPost<{ id: number }>(
      `/api/reporting/clients/${projectId}/reports/${id}/duplicate`,
      {},
    )
    setDuplicatingId(null)
    if (data) {
      navigate(`/p/${projectId}/reports/${data.id}`)
    }
  }

  return (
    <div className="text-[#1B2559]">
      {/* Toast confirmation "enregistré comme template" */}
      {savedTemplateName && (
        <div
          role="status"
          className="mb-4 bg-[#6366F1]/10 text-[#4F46E5] rounded-lg px-3 py-2 text-sm"
        >
          Template «&nbsp;{savedTemplateName}&nbsp;» enregistré.
        </div>
      )}

      {/* Page title */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-[#1B2559]">Rapports</h2>
          <p className="text-[#8B8FA7] text-sm mt-0.5">Créez et gérez vos rapports</p>
        </div>
        {!showCreate && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#6366F1] text-white text-sm font-medium rounded-xl hover:bg-[#5558E6] transition-colors"
          >
            <Plus className="w-4 h-4" strokeWidth={1.25} />
            Nouveau rapport
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white border border-[#E2E4ED] rounded-2xl p-5 mb-8">
          <h3 className="font-semibold text-[#1B2559] mb-4">Nouveau rapport</h3>
          <div className="flex items-center gap-6 mb-4">
            <label className="flex items-center gap-2 text-sm text-[#1B2559] cursor-pointer">
              <input
                type="radio"
                name="create-mode"
                checked={mode === 'blank'}
                onChange={() => setMode('blank')}
                className="accent-orion-primary"
              />
              Vierge
            </label>
            <label className="flex items-center gap-2 text-sm text-[#1B2559] cursor-pointer">
              <input
                type="radio"
                name="create-mode"
                checked={mode === 'template'}
                onChange={() => setMode('template')}
                className="accent-orion-primary"
              />
              Depuis un template
            </label>
          </div>
          <form onSubmit={handleCreate} className="flex items-start gap-3">
            {mode === 'blank' ? (
              <input
                autoFocus
                type="text"
                value={creatingName}
                onChange={(e) => setCreatingName(e.target.value)}
                placeholder="Nom du rapport"
                className="flex-1 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl px-4 py-2.5 text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary transition-colors"
              />
            ) : (
              <select
                aria-label="Template"
                value={selectedTemplateId ?? ''}
                onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
                className="flex-1 bg-[#F4F5FA] border border-[#E2E4ED] rounded-xl px-4 py-2.5 text-sm text-[#1B2559] focus:outline-none focus:border-orion-primary transition-colors"
              >
                <option value="">Choisir un template…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2.5 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {creating ? 'Création…' : 'Créer'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowCreate(false)
                setCreatingName('')
                setCreateError(null)
                setMode('blank')
                setSelectedTemplateId(null)
              }}
              className="px-4 py-2.5 text-sm text-[#8B8FA7] hover:text-[#1B2559] transition-colors"
            >
              Annuler
            </button>
          </form>
          {createError && <p className="text-score-critical text-sm mt-2">{createError}</p>}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {!loading && error && (
        <div className="bg-score-critical/10 border border-score-critical/20 rounded-xl p-4 text-score-critical text-sm">
          Erreur : {error}
        </div>
      )}

      {!loading && !error && (
        <>
          {asArray(reports).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {asArray(reports).map((r) => (
                <div
                  key={r.id}
                  className="group relative bg-white border border-[#E2E4ED] rounded-xl overflow-hidden hover:border-[#6366F1] transition-colors flex flex-col"
                >
                  {/* Miniature live (page 0 du rapport) — le Link couvre la zone cliquable */}
                  <Link
                    to={`/p/${projectId}/reports/${r.id}`}
                    className="relative block h-44 overflow-hidden rounded-t-xl bg-[#F4F5FA] border-b border-[#E2E4ED]"
                  >
                    {/* Accent couleur en haut de la miniature */}
                    <div
                      className="absolute top-0 left-0 right-0 h-1 z-10"
                      style={{ backgroundColor: r.headerColor || '#6366F1' }}
                    />
                    <iframe
                      src={`/p/${projectId}/reports/${r.id}/embed`}
                      title={r.name}
                      loading="lazy"
                      tabIndex={-1}
                      aria-hidden="true"
                      className="pointer-events-none origin-top-left"
                      style={{ width: 1100, height: 1556, transform: `scale(${THUMB_SCALE})` }}
                    />
                  </Link>

                  {/* Corps : nom + période + date de modif */}
                  <Link to={`/p/${projectId}/reports/${r.id}`} className="block p-4 flex-1">
                    <p className="font-semibold text-[#1B2559] text-sm mb-1 group-hover:text-[#6366F1]">
                      {r.name}
                    </p>
                    <p className="text-[10px] text-[#8B8FA7]">
                      {r.startDate && r.endDate
                        ? `${new Date(r.startDate).toLocaleDateString('fr-FR')} — ${new Date(
                            r.endDate,
                          ).toLocaleDateString('fr-FR')}`
                        : 'Période non définie'}
                    </p>
                    <p className="text-[10px] text-[#B0B4C5] mt-1">
                      Modifié {new Date(r.updatedAt || r.createdAt).toLocaleDateString('fr-FR')}
                    </p>
                  </Link>

                  {/* Barre d'actions — toujours visible, ne chevauche plus rien */}
                  <div className="flex items-center justify-end gap-1 px-4 pb-3 border-t border-[#E2E4ED] pt-2">
                    <button
                      onClick={(e) => handleSaveAsTemplate(e, r.id)}
                      disabled={savingTemplateId === r.id}
                      aria-label="Enregistrer comme template"
                      className="p-1.5 text-[#555A73] hover:text-[#6366F1] hover:bg-[#6366F1]/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <BookmarkPlus className="w-4 h-4" strokeWidth={1.25} />
                    </button>
                    <button
                      onClick={(e) => handleDuplicate(e, r.id)}
                      disabled={duplicatingId === r.id}
                      aria-label="Dupliquer"
                      className="p-1.5 text-[#555A73] hover:text-[#6366F1] hover:bg-[#6366F1]/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Copy className="w-4 h-4" strokeWidth={1.25} />
                    </button>
                    <button
                      onClick={(e) => handleDelete(e, r.id)}
                      disabled={deletingId === r.id}
                      aria-label="Supprimer"
                      className="p-1.5 text-[#555A73] hover:text-score-critical hover:bg-score-critical/10 rounded-lg transition-colors disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.25} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
                <FileText className="w-7 h-7 text-[#555A73]" strokeWidth={1.25} />
              </div>
              <p className="text-[#1B2559] font-semibold mb-1">Aucun rapport</p>
              <p className="text-[#8B8FA7] text-sm mb-6">Créez votre premier rapport.</p>
              <button
                onClick={() => setShowCreate(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity"
              >
                <Plus className="w-4 h-4" strokeWidth={1.25} />
                Nouveau rapport
              </button>
            </div>
          )}
        </>
      )}

      {mappingPlan && (
        <TemplateMappingModal
          plan={mappingPlan}
          onConfirm={handleConfirmMapping}
          onCancel={() => setMappingPlan(null)}
        />
      )}
    </div>
  )
}
